// Geolocation detection service for regional currency handling

interface GeolocationData {
  country: string;
  countryCode: string;
  region: string;
  city: string;
  currency: string;
  timezone: string;
  ip: string;
}

class GeolocationService {
  private cached: GeolocationData | null = null;
  private cacheKey = 'user_geolocation';
  private cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Detect user's location and determine if they're from Georgia
   */
  async detectLocation(): Promise<GeolocationData> {
    // Check cache first
    const cached = this.getCachedLocation();
    if (cached) {
      console.log('🌍 Using cached location:', cached);
      return cached;
    }

    console.log('🌍 Detecting location...');

    // Try multiple geolocation services for production reliability
    const apis = [
      () => this.tryIpApiCom(),
      () => this.tryIpwhois(),
      () => this.tryIpApiCo(),
      () => this.tryCloudflareTrace(),
      () => this.tryTimezoneByIP()
    ];

    for (const apiCall of apis) {
      try {
        const result = await apiCall();
        if (result) {
          console.log('🌍 Location detected successfully:', result);
          this.cacheLocation(result);
          this.cached = result;
          return result;
        }
      } catch (error) {
        console.warn('🌍 Geolocation API failed, trying next...', error);
        continue;
      }
    }

    // Final fallback using browser timezone hint
    console.warn('🌍 All geolocation APIs failed, using timezone fallback');
    return this.getTimezoneBasedFallback();
  }

  /**
   * Check if user is from Georgia
   */
  async isFromGeorgia(): Promise<boolean> {
    const location = await this.detectLocation();
    return location.countryCode === 'GE';
  }

  /**
   * Get user's preferred currency based on location
   */
  async getUserCurrency(): Promise<'USD' | 'GEL'> {
    const isGeorgian = await this.isFromGeorgia();
    return isGeorgian ? 'GEL' : 'USD';
  }

  /**
   * Get cached location data
   */
  private getCachedLocation(): GeolocationData | null {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - parsed.timestamp > this.cacheExpiry) {
        localStorage.removeItem(this.cacheKey);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error('Error reading cached location:', error);
      return null;
    }
  }

  /**
   * Cache location data
   */
  private cacheLocation(data: GeolocationData): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching location data:', error);
    }
  }

  /**
   * Clear cached location data
   */
  clearCache(): void {
    localStorage.removeItem(this.cacheKey);
    this.cached = null;
  }

  /**
   * Try ip-api.com (HTTPS version) 
   */
  private async tryIpApiCom(): Promise<GeolocationData | null> {
    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (data.country_code) {
        return {
          country: data.country_name || 'Unknown',
          countryCode: data.country_code.toUpperCase(),
          region: data.region || '',
          city: data.city || '',
          currency: data.country_code.toUpperCase() === 'GE' ? 'GEL' : 'USD',
          timezone: data.timezone || '',
          ip: data.ip || ''
        };
      }
    } catch (error) {
      console.warn('🌍 ipapi.co failed:', error);
    }
    return null;
  }

  /**
   * Try ipwhois.app API
   */
  private async tryIpwhois(): Promise<GeolocationData | null> {
    try {
      const response = await fetch('https://ipwho.is/', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (data.country_code) {
        return {
          country: data.country || 'Unknown',
          countryCode: data.country_code.toUpperCase(),
          region: data.region || '',
          city: data.city || '',
          currency: data.country_code.toUpperCase() === 'GE' ? 'GEL' : 'USD',
          timezone: data.timezone?.id || '',
          ip: data.ip || ''
        };
      }
    } catch (error) {
      console.warn('🌍 ipwho.is failed:', error);
    }
    return null;
  }

  /**
   * Try ip-api.com (different endpoint)
   */
  private async tryIpApiCo(): Promise<GeolocationData | null> {
    try {
      const response = await fetch('http://ip-api.com/json/?fields=status,country,countryCode,regionName,city,timezone,query', {
        method: 'GET'
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (data.status === 'success' && data.countryCode) {
        return {
          country: data.country || 'Unknown',
          countryCode: data.countryCode.toUpperCase(),
          region: data.regionName || '',
          city: data.city || '',
          currency: data.countryCode.toUpperCase() === 'GE' ? 'GEL' : 'USD',
          timezone: data.timezone || '',
          ip: data.query || ''
        };
      }
    } catch (error) {
      console.warn('🌍 ip-api.com failed:', error);
    }
    return null;
  }

  /**
   * Try Cloudflare trace (very reliable)
   */
  private async tryCloudflareTrace(): Promise<GeolocationData | null> {
    try {
      const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const text = await response.text();
      const lines = text.split('\n');
      const data: any = {};
      
      lines.forEach(line => {
        const [key, value] = line.split('=');
        if (key && value) data[key] = value;
      });
      
      if (data.loc) {
        return {
          country: data.loc === 'GE' ? 'Georgia' : 'Unknown',
          countryCode: data.loc.toUpperCase(),
          region: '',
          city: '',
          currency: data.loc.toUpperCase() === 'GE' ? 'GEL' : 'USD',
          timezone: '',
          ip: data.ip || ''
        };
      }
    } catch (error) {
      console.warn('🌍 Cloudflare trace failed:', error);
    }
    return null;
  }

  /**
   * Try timezone-based detection as fallback
   */
  private async tryTimezoneByIP(): Promise<GeolocationData | null> {
    try {
      const response = await fetch('https://worldtimeapi.org/api/ip');
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      // Georgia uses Asia/Tbilisi timezone
      if (data.timezone === 'Asia/Tbilisi') {
        return {
          country: 'Georgia',
          countryCode: 'GE',
          region: '',
          city: '',
          currency: 'GEL',
          timezone: data.timezone,
          ip: data.client_ip || ''
        };
      }
    } catch (error) {
      console.warn('🌍 WorldTimeAPI failed:', error);
    }
    return null;
  }

  /**
   * Last resort: browser timezone-based fallback
   */
  private getTimezoneBasedFallback(): GeolocationData {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('🌍 Browser timezone:', timezone);
      
      // Georgia uses Asia/Tbilisi timezone
      const isGeorgia = timezone === 'Asia/Tbilisi';
      
      return {
        country: isGeorgia ? 'Georgia' : 'Unknown',
        countryCode: isGeorgia ? 'GE' : 'US',
        region: '',
        city: '',
        currency: isGeorgia ? 'GEL' : 'USD',
        timezone: timezone,
        ip: ''
      };
    } catch (error) {
      console.error('🌍 Even timezone detection failed:', error);
      
      // Absolute fallback
      return {
        country: 'Unknown',
        countryCode: 'US',
        region: '',
        city: '',
        currency: 'USD',
        timezone: '',
        ip: ''
      };
    }
  }

}

// Export singleton instance
export const geolocationService = new GeolocationService();

// Export types
export type { GeolocationData };
